import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { plainToClass } from 'class-transformer';
import { EntityRelation } from '../../shared/models/entity-relation.model';
import { ApplicationTypeConstants, WarmUpService } from '../../services/warmup/warmup.service';
import { ApplicationValue } from '../../services/warmup/application-type.model';

export enum RelationshipPanelActions {
  UPDATE_RELATIONSHIPS = <any>'updateRelationships',
  LOADING_FILE = <any>'loading',
  STOP_LOADING_FILE = <any>'stopLoading'
}

export enum MetaForms {
  SHAREHOLDER, DIRECTOR, OTHER
}

/**
 * Service for entity relationship tab.
 */
@Injectable()
export class EntityRelationsService {
  entityDetailsUpdated: EventEmitter<EntityIdDetails> = new EventEmitter();
  private broadCastMeaasge = new BehaviorSubject<string>('old');
  currentMessage = this.broadCastMeaasge.asObservable();
  private entityIdDetails: EntityIdDetails;

  constructor(private requestService: ObservableRequestService,
    private warmupService: WarmUpService) {
    this.entityIdDetails = new EntityIdDetails();
  }

  setEntityIdDetails(entityIdDetails: EntityIdDetails): void {
    this.entityIdDetails = entityIdDetails;
    this.entityDetailsUpdated.emit(this.entityIdDetails);
  }

  getEntityIdDetails(): EntityIdDetails {
    return this.entityIdDetails;
  }

  loadReverseRelationshipDetails(id: number): Observable<any> {
    return this.requestService.getRequest(
      ConstantsService.REVERSE_RELATIONSHIP + id);
  }

  getRelationships(entityID: Number): Observable<any> {
    return this.requestService.getRequest(
      `${ConstantsService.RELATIONSHIP}/${entityID}`)
      .map(response => {
        return plainToClass(EntityRelation, response.arrResult as Object[]);
      });
  }

  getRelationshipsWithOffsetAndLimits(entityID: Number, offset: Number, limit: Number): Observable<any> {
    return this.requestService.getRequest(
      `${ConstantsService.RELATIONSHIP}/${entityID}?offset=${offset}&limit=${limit}`);
  }

  deleteRelation(relationId: String): Observable<any> {
    return this.requestService.deleteRequest(
      `${ConstantsService.RELATIONSHIP}/${relationId}`, '', this.requestService.getJSONContentType());
  };

  sendMessage(message: string) {
    this.broadCastMeaasge.next(message);
  }

  addRelation(subjectEntityId, body): Observable<any> {
    return this.requestService.postRequest(
      `${ConstantsService.RELATIONSHIP}/${subjectEntityId}`,
      JSON.stringify(body),
      this.requestService.getJSONContentType());
  }

  updateRelation(relationId, relationVo): Observable<any> {
    return this.requestService.putRequest(
      `${ConstantsService.RELATIONSHIP}/${relationId}`,
      JSON.stringify(relationVo),
      this.requestService.getJSONContentType());
  }

  updateExecutiveSummary(executiveSummary, entityId, entityType): Observable<any> {
    return this.requestService.putRequest(
      `${ConstantsService.EXECUTIVE_SUMMARY}/${entityId}/${entityType}`,
      JSON.stringify(executiveSummary),
      this.requestService.getJSONContentType());
  }

  markUbo(relationId, subjectId, inRoleId): Observable<any> {
    return this.requestService.putRequest(
      `${ConstantsService.UBO_RELATIONSHIP}/${relationId}/${subjectId}/${inRoleId}`, null,
      this.requestService.getJSONContentType());
  }

  loadRelationTypes() {
    const relations =
      this.warmupService.getApplicationValue(ApplicationTypeConstants.RELATION_ROLE_TYPES);
    const oppositeRelations =
      this.warmupService.getApplicationValue(ApplicationTypeConstants.OPPOSITE_RELATION_ROLE_TYPES);
    let allRelations: ApplicationValue[];
    if (relations instanceof Observable && oppositeRelations instanceof Observable) {
      relations.concat(oppositeRelations).subscribe(
        response => {
          this.warmupService.setApplicationTypeInLS(response);
          return response.values;
        }
      );
    } else if (!(oppositeRelations instanceof Observable) && !(relations instanceof Observable)) {
      let relationsArr: ApplicationValue[] = relations;
      let oppRelationsArr: ApplicationValue[] = oppositeRelations;
      return relationsArr.concat(oppRelationsArr);
    } else if (!(oppositeRelations instanceof Observable)) {
      relations.subscribe(
        response => {
          this.warmupService.setApplicationTypeInLS(response);
          return response.values.concat(oppositeRelations);
        }
      );
    } else if (!(relations instanceof Observable)) {
      oppositeRelations.subscribe(
        response => {
          this.warmupService.setApplicationTypeInLS(response);
          return relations.concat(response.values);
        }
      );
    }
  }

  getMetaFormType(relationName: String): MetaForms {
    if (relationName === 'Shareholder' || relationName === 'General Partner'
      || relationName === 'Limited Partner' || relationName === 'Member' || relationName === 'Nominee shareholder') {
      return MetaForms.SHAREHOLDER;
    } else if (relationName === 'Director') {
      return MetaForms.DIRECTOR;
    }
    return MetaForms.OTHER;
  }

  getEntityRelationShipDiagram(entityId: number): Observable<any> {
    return this.requestService.getRequest(
      ConstantsService.ENTITY + "/" + entityId + ConstantsService.RELATIONSHIP_DIAGRAM);
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/relations/entity-relations.service.ts